import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import ValidatedField from '@/features/listings/components/ValidatedField';

const EMPTY = { note: '', preferredWindow: '', deliverTo: '' };

// Mirrors createSchema in server/src/api/request/request.validation.js: the
// note is the whole ask, so an empty one is refused here rather than costing
// a round trip that loses what the member typed.
const NOTE_REQUIRED = 'Tell the poster why you need it.';

function RequestForm({
    show,
    listingTitle,
    submitting = false,
    error = '',
    onCancel,
    onSubmit,
}) {
    const [form, setForm] = useState(EMPTY);
    const [noteError, setNoteError] = useState('');

    // Reopening starts a new request rather than resuming the last one, which
    // matters after a send: the fields would otherwise still hold the text of
    // a request the member already made.
    useEffect(() => {
        if (show) {
            setForm(EMPTY);
            setNoteError('');
        }
    }, [show]);

    const change = (field) => (event) => {
        const { value } = event.target;
        setForm((previous) => ({ ...previous, [field]: value }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!form.note.trim()) {
            setNoteError(NOTE_REQUIRED);
            return;
        }
        setNoteError('');
        onSubmit({
            note: form.note.trim(),
            preferredWindow: form.preferredWindow.trim(),
            deliverTo: form.deliverTo.trim(),
        });
    };

    return (
        <Modal
            show={show}
            onHide={onCancel}
            aria-labelledby="request-form-title"
        >
            <Form noValidate onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title as="h2" className="h5" id="request-form-title">
                        Request {listingTitle}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <ValidatedField
                        required
                        controlId="request-note"
                        label="Why you need it"
                        as="textarea"
                        rows={4}
                        value={form.note}
                        onChange={change('note')}
                        error={noteError}
                        help="The poster reads this when they decide."
                    />
                    <ValidatedField
                        controlId="request-preferred-window"
                        label="Preferred pickup window"
                        value={form.preferredWindow}
                        onChange={change('preferredWindow')}
                        help="Optional. When you could collect it."
                    />
                    <ValidatedField
                        controlId="request-deliver-to"
                        label="Deliver to"
                        value={form.deliverTo}
                        onChange={change('deliverTo')}
                        help="Optional. The building and room it is headed for."
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={submitting}>
                        Send request
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

RequestForm.propTypes = {
    show: PropTypes.bool,
    listingTitle: PropTypes.string,
    submitting: PropTypes.bool,
    error: PropTypes.string,
    onCancel: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
};

export default RequestForm;
